import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import Circle from 'ol/style/Circle';
import MultiPoint from 'ol/geom/MultiPoint';
import { GEOMETRY_TYPE, COLOR } from '@tmapy/config';
import { createInstanceFactory } from '@tmapy/utils';
import { getStyles, getDrawStyles } from './style/measure';
import { getArrowsStyles } from './style/arrow';
import { getTextStyle } from './style/text';
import { adjustRotateData, getCommonRotateData } from './style/rotateText';
import { LINE_DASH, STYLE } from '../style';
const strokeFactory = createInstanceFactory((config) => new Stroke(config));
const fillFactory = createInstanceFactory((config) => new Fill(config));
export const styleFactory = (config) => {
    if (config === undefined) {
        return STYLE.DEFAULT;
    }
    // TODO: this does not support nested arrays and functions
    if (Array.isArray(config)) {
        return config.map(styleFactory);
    }
    if (typeof config === 'string' && STYLE[config]) {
        return STYLE[config];
    }
    // TODO: Cannot be used in array
    if (config === 'MEASURE:polygon') {
        return (feature, resolution) => [
            ...createDefaultStyle({ color: COLOR.ACTIVE, lineDash: LINE_DASH }),
            ...getDrawStyles(feature, resolution, 'EPSG:5514', {
                color: COLOR.ACTIVE,
            }, GEOMETRY_TYPE.POLYGON),
        ];
    }
    if (config === 'MEASURE:line') {
        return (feature, resolution) => [
            ...createDefaultStyle({ color: COLOR.ACTIVE, lineDash: LINE_DASH }),
            ...getDrawStyles(feature, resolution, 'EPSG:5514', {
                color: COLOR.ACTIVE,
            }, GEOMETRY_TYPE.LINE_STRING),
        ];
    }
    if (config === 'MEASURE') {
        return (feature, resolution) => [
            ...createDefaultStyle({ color: COLOR.ACTIVE, lineDash: LINE_DASH }),
            ...getStyles(feature, resolution, 'EPSG:5514', {
                color: COLOR.ACTIVE,
            }),
        ];
    }
    if (config === 'NOTE') {
        return (feature, resolution) => {
            const isText = feature.get('type') === GEOMETRY_TYPE.TEXT;
            const text = feature.get('label') ?? '';
            const geometry = feature.getGeometry();
            const geometryType = feature.getGeometry().getType();
            if (geometryType === GEOMETRY_TYPE.LINE_STRING) {
                const coords = geometry.getCoordinates();
                const { rotation, coordinate } = getCommonRotateData(coords[0], coords[1]);
                const textRotation = adjustRotateData(rotation, coordinate, resolution);
                return [
                    ...createDefaultStyle({ color: COLOR.DEFAULT }),
                    ...getTextStyle({
                        text,
                        geometry: textRotation.point,
                        rotation: textRotation.rotation,
                        color: COLOR.DEFAULT,
                        overflow: true,
                        textAlign: 'center',
                        placement: 'line',
                    }),
                ];
            }
            else if (geometryType === GEOMETRY_TYPE.POLYGON) {
                return [
                    ...createDefaultStyle({ color: COLOR.DEFAULT }),
                    ...getTextStyle({
                        text,
                        geometry,
                        rotation: 0,
                        color: COLOR.DEFAULT,
                        overflow: true,
                        textAlign: 'center',
                    }),
                ];
            }
            else if (geometryType === GEOMETRY_TYPE.POINT && isText && text) {
                return [
                    ...getTextStyle({
                        text,
                        geometry,
                        rotation: 0,
                        color: COLOR.DEFAULT,
                        overflow: true,
                        textAlign: 'center',
                    }),
                ];
            }
            else {
                return [
                    ...createDefaultStyle({ color: COLOR.DEFAULT }),
                    ...getTextStyle({
                        text,
                        geometry,
                        rotation: 0,
                        color: COLOR.DEFAULT,
                        overflow: true,
                        textAlign: 'left',
                        offsetX: 10,
                    }),
                ];
            }
        };
    }
    if (config === 'NOTE.active') {
        return (feature, resolution) => {
            const isText = feature.get('type') === GEOMETRY_TYPE.TEXT;
            const text = feature.get('label') ?? '';
            const geometry = feature.getGeometry();
            const geometryType = feature.getGeometry().getType();
            if (geometryType === GEOMETRY_TYPE.LINE_STRING) {
                const coords = geometry.getCoordinates();
                const { rotation, coordinate } = getCommonRotateData(coords[0], coords[1]);
                const textRotation = adjustRotateData(rotation, coordinate, resolution);
                return [
                    ...createDefaultStyle({ color: COLOR.ACTIVE }),
                    ...getTextStyle({
                        text,
                        geometry: textRotation.point,
                        rotation: textRotation.rotation,
                        color: COLOR.ACTIVE,
                        overflow: true,
                        textAlign: 'center',
                        placement: 'line',
                    }),
                ];
            }
            else if (geometryType === GEOMETRY_TYPE.POLYGON) {
                return [
                    ...createDefaultStyle({ color: COLOR.ACTIVE }),
                    ...getTextStyle({
                        text,
                        geometry,
                        rotation: 0,
                        color: COLOR.ACTIVE,
                        overflow: true,
                        textAlign: 'center',
                    }),
                ];
            }
            else if (geometryType === GEOMETRY_TYPE.POINT && isText && text) {
                return [
                    ...getTextStyle({
                        text,
                        geometry,
                        rotation: 0,
                        color: COLOR.ACTIVE,
                        overflow: true,
                        textAlign: 'center',
                    }),
                ];
            }
            else {
                return [
                    ...createDefaultStyle({ color: COLOR.ACTIVE }),
                    ...getTextStyle({
                        text,
                        geometry,
                        rotation: 0,
                        color: COLOR.ACTIVE,
                        overflow: true,
                        textAlign: 'left',
                        offsetX: 10,
                    }),
                ];
            }
        };
    }
    if (config === 'ARROW.highlight') {
        return (feature) => {
            const text = feature.get('label') ?? '';
            const geometry = feature.getGeometry();
            return [
                ...createDefaultStyle({ color: COLOR.HIGHLIGHT }),
                ...getArrowsStyles(feature, {
                    color: COLOR.HIGHLIGHT,
                    width: 2,
                }),
                ...getTextStyle({
                    text,
                    geometry,
                    rotation: 0,
                    color: COLOR.HIGHLIGHT,
                    overflow: true,
                    textAlign: 'left',
                    offsetX: 10,
                }),
            ];
        };
    }
    if (typeof config === 'object') {
        return new Style({
            ...config,
            stroke: config.stroke && strokeFactory(config.stroke),
            fill: config.fill && fillFactory(config.fill),
        });
    }
    return STYLE.DEFAULT;
};
export const createDefaultStyle = (params) => {
    const style = [
        // cursor outline
        new Style({
            image: new Circle({
                fill: new Fill({
                    color: [255, 255, 255],
                }),
                radius: 6,
            }),
        }),
        // cursor
        new Style({
            image: new Circle({
                fill: new Fill({
                    ...params,
                }),
                radius: 5,
            }),
        }),
        // polygon fill
        new Style({
            fill: new Fill({
                color: [255, 255, 255, 0.5],
            }),
        }),
        // polygon stroke outline
        new Style({
            stroke: new Stroke({
                ...params,
                color: [255, 255, 255],
                width: 5,
            }),
        }),
        // polygon stroke
        new Style({
            stroke: new Stroke({
                ...params,
                width: 3,
            }),
        }),
    ];
    if (params.color === COLOR.ACTIVE) {
        style.push(createVertexStyles({ color: params.color }));
    }
    return style;
};
const createVertexStyles = (style) => {
    return new Style({
        image: new Circle({
            radius: 3,
            fill: new Fill({
                color: [255, 255, 255],
            }),
            stroke: new Stroke({ color: style.color, width: 2 }),
        }),
        geometry: (feature) => {
            if (!feature)
                return undefined;
            const geometry = feature.getGeometry();
            if (!geometry)
                return undefined;
            const geomType = geometry.getType();
            let coordinates;
            switch (geomType) {
                case 'LineString': {
                    coordinates = geometry.getCoordinates();
                    break;
                }
                case 'MultiLineString':
                case 'Polygon': {
                    coordinates = geometry.getCoordinates().flat();
                    break;
                }
                case 'MultiPolygon': {
                    coordinates = geometry.getCoordinates().flat().flat();
                    break;
                }
            }
            if (!coordinates)
                return undefined;
            return new MultiPoint(coordinates);
        },
    });
};
export const styleFilter = (predicate, style, defaultStyle) => (feature, resolution) => {
    if (predicate(feature, resolution)) {
        if (typeof style === 'function')
            return style(feature, resolution);
        return style ?? [];
    }
    return defaultStyle
        ? typeof defaultStyle === 'function'
            ? defaultStyle(feature, resolution)
            : defaultStyle
        : [];
};
//# sourceMappingURL=styleFactory.js.map